import * as React from 'react';
import { useState, useEffect } from 'react';

/** Material UI Packages. */
import { Box } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';

/** Redux Packages. */
import { useSelector, useDispatch } from 'react-redux';

/** Internal Components. */
import { containerSizeList } from 'shared/config/constantsData';
import { handleTabKeyDown } from 'shared/ui/Datagrid';
import { VolumeAutocomplete } from 'shared/ui/AutoCompleteForm';
import EditToolbar from 'shared/ui/EditToolbar';
import { readSocketData } from 'shared/lib/fetchSocket';
import useInvalidOptionWarning from 'shared/hooks/useInvalidOptionWarning';

import { cntrCols, exportCntrCols, aliasCols, truckingAliasCols } from './TableDef';
import { actionMap } from './ActionMap';
import { FieldRenderer } from './FieldRenderer';

/** Initial values for autocomplete options. */
const CONTAINER_INITIAL_STATE = {
    il_trade_partner_id_shipper: { inputValue: '', options: [] },
    il_trade_partner_id_consignee: { inputValue: '', options: [] },
    il_opus_volume_unit: { inputValue: '', options: [] },
};

const DATAGRID_CONFIG = {
    height: '260px',
    fontSize: 11,
    borderRadius: '10px',
    overflow: 'auto',
    '& .MuiDataGrid-columnHeader': { backgroundColor: '#dfdfdf' },
    '& .MuiDataGrid-cell': { border: '0.5px solid #dfdfdf' },
    '& .MuiSelect-select': { fontSize: 13 },
    '& .MuiDataGrid-row--editing .MuiDataGrid-cell': { backgroundColor: '#c3cdd0' },
};

export default function NewShipmentContainer(props) {
    /** Get props from ShipmentControls.js */
    const { dialogConfig, gridApiRef } = props;
    /** Get props from NewShipmentHouse.js */
    const { tabId, setGridEditingKeys } = props;

    /** Get the dispatch function from Redux to send actions to the store. */
    const dispatch = useDispatch();

    /** Get the selected shipment tab value from Redux state. */
    const tabValue = useSelector((state) => state.tabValueForm);

    /** Autocomplete options state (e.g. shipper, consignee, volume unit). */
    const [autocompleteOptions, setAutocompleteOptions] = useState(CONTAINER_INITIAL_STATE);
    /** Row edit/view state in DataGrid. */
    const [rowModesModel, setRowModesModel] = useState({});
    /** Warn the user when a pasted autocomplete/select value isn't a valid option. */
    const handleInvalidOption = useInvalidOptionWarning();

    /** Flags for shipment type. */
    const isTrucking = dialogConfig.type === 'trucking';
    const isOcean = dialogConfig.type === 'ocean';
    const isAir = dialogConfig.type === 'air';
    const isOex = dialogConfig.tableName === 'il_ocean_export';

    /** Helper to access main list from redux store safely. */
    const getMainList = (state) => {
        const formState = state[dialogConfig.formState];
        return formState?.[dialogConfig.keyName[0]];
    };

    /** Get HBL row (only for ocean or air shipments). */
    const hblRow = useSelector((state) => {
        if (isTrucking) return [];
        const list = getMainList(state);
        return Array.isArray(list) ? list.find((item) => item.id === tabId) || [] : [];
    });
    /** Get container row (only for ocean or air shipments). */
    const cntrRow = useSelector((state) => {
        if (isTrucking) return [];
        const list = getMainList(state);
        const found = Array.isArray(list) ? list.find((item) => item.id === tabId) : null;
        return found?.[dialogConfig.keyName[1]] || [];
    });
    /** Get alias row (only for trucking shipments). */
    const aliasRow = useSelector((state) => (isTrucking ? state.truckingNewShipmentForm.il_ftl_ltl_alias || [] : []));

    /** Handle to change a autocomplete component value when value should be ojbect. */
    const handleAutocompleteChange = (fieldName) => (event, value) => {
        dispatch(
            actionMap[dialogConfig.reducerActionsHBL[3]]({ id: tabId, key: fieldName, value: value ? value : '' })
        );
    };

    /** Request the option list for the value from the server via WebSocket. */
    const fetchAutocompleteOptions = (key, value, type) => {
        readSocketData(dispatch, tabValue, 'cell', type, key, value, (receivedData) => {
            setAutocompleteOptions((prev) => ({
                ...prev,
                [key]: { ...prev[key], options: receivedData },
            }));
        });
    };

    /** Handle to change options list using websocket. */
    const handleOptionsChange = (key, event, value, type) => {
        setAutocompleteOptions((prev) => ({
            ...prev,
            [key]: { ...prev[key], inputValue: value, options: [] },
        }));
        fetchAutocompleteOptions(key, value, type);
    };

    /** Handle to change autocomplete location value and call Websocket to receive the searched result. */
    const handleUnitChange = (event, value, key) => {
        readSocketData(dispatch, tabValue, 'cell', key, '', value, (receivedData) => {
            setAutocompleteOptions((prev) => ({
                ...prev,
                [key]: { ...prev[key], options: receivedData },
            }));
        });
    };

    /** Prefetch unit options as soon as the row enters edit mode, before the user types.
     *  editMode is 'row' here, so cellEditStart is never published - use rowEditStart instead,
     *  and read the value off params.row since GridRowParams has no per-field value. */
    const handleRowEditStart = (params) => {
        if (params.field === 'il_opus_volume_unit_id') {
            handleUnitChange(null, params.row.il_opus_volume_unit_id || '', 'il_opus_volume_unit');
        }
    };

    /** Handle delete action per row depending on shipment type. */
    const handleDeleteClick = (id) => () => {
        const deleteAction = actionMap[dialogConfig.reducerActionsCNTR[2]];
        if (isOcean) {
            dispatch(deleteAction({ hblId: tabId, containerId: id }));
        } else if (isAir) {
            dispatch(deleteAction({ hawbId: tabId, aliasId: id }));
        } else {
            dispatch(deleteAction(id));
        }
    };

    /** Validate row values before dispatching update. */
    const validateRow = (row) => {
        if (isAir || isTrucking) {
            const volumeField = isOcean ? 'volume' : 'quantity';
            const value = row[volumeField];
            if (!Number.isInteger(value) || value < 0 || !Number.isInteger(row.weight) || row.weight < 0) {
                throw new Error('Volume and Weight must be a positive number.');
            }
        }
    };

    /** Handle row update (dispatch update to Redux). */
    const processRowUpdate = (newRow) => {
        validateRow(newRow);

        const sendValue = isOcean
            ? {
                  hblId: tabId,
                  containerId: newRow.id,
                  updatedContainer: isOex
                      ? { cntr_number: newRow.cntr_number, seal_number: newRow.seal_number, volume: newRow.volume }
                      : { cntr_number: newRow.cntr_number, volume: newRow.volume },
              }
            : isAir
            ? {
                  hawbId: tabId,
                  aliasId: newRow.id,
                  updatedAlias: {
                      alias: newRow.alias,
                      il_opus_vessel_id: newRow.il_opus_vessel_id,
                      quantity: newRow.quantity,
                      il_opus_volume_unit_id: newRow.il_opus_volume_unit_id,
                      weight: newRow.weight,
                  },
              }
            : {
                  aliasId: newRow.id,
                  updatedAlias: {
                      alias: newRow.alias,
                      quantity: newRow.quantity,
                      il_opus_volume_unit_id: newRow.il_opus_volume_unit_id,
                      weight: newRow.weight,
                      reference: newRow.reference,
                  },
              };

        dispatch(actionMap[dialogConfig.reducerActionsCNTR[1]](sendValue));
        return { ...newRow };
    };

    /** Render volume unit cell using custom Autocomplete. */
    const renderVolumeEditCell = (params) => (
        <VolumeAutocomplete {...params} rows={cntrRow} options={containerSizeList} />
    );

    /** Get current rows depending on shipment type. */
    const getRows = () => (isTrucking ? aliasRow : cntrRow);
    /** Get DataGrid column definition based on shipment type. */
    const getColumns = () =>
        isOcean
            ? isOex
                ? exportCntrCols(handleDeleteClick, renderVolumeEditCell, handleInvalidOption)
                : cntrCols(handleDeleteClick, renderVolumeEditCell, handleInvalidOption)
            : isAir
            ? aliasCols(cntrRow, autocompleteOptions, handleUnitChange, handleDeleteClick, handleInvalidOption)
            : truckingAliasCols(aliasRow, autocompleteOptions, handleUnitChange, handleDeleteClick, handleInvalidOption);

    /** Keep track of which rows are currently being edited. */
    useEffect(() => {
        const editingKeys = Object.entries(rowModesModel)
            .filter(([_, model]) => model.mode === 'edit')
            .map(([key]) => key);
        setGridEditingKeys(editingKeys);
    }, [rowModesModel]);

    return (
        <>
            {!isTrucking && (
                <FieldRenderer.ContainerAutocompleteFields
                    field={{ key: 'container-autocomplete-fields' }}
                    dialogConfig={dialogConfig}
                    hblRow={hblRow}
                    handleAutocompleteChange_obj={handleAutocompleteChange}
                    handleOptionsChange={handleOptionsChange}
                    autocompleteOptions={autocompleteOptions}
                />
            )}
            <DataGridPremium
                rows={getRows()}
                columns={getColumns()}
                density="compact"
                editMode="row"
                initialState={{ columns: { columnVisibilityModel: { id: false } } }}
                disableColumnMenu
                disableColumnSorting
                hideFooter
                rowModesModel={rowModesModel}
                processRowUpdate={processRowUpdate}
                onProcessRowUpdateError={(err) => alert(err.message)}
                onRowModesModelChange={setRowModesModel}
                slots={{ toolbar: EditToolbar }}
                slotProps={{
                    toolbar: {
                        setRowModesModel,
                        dispatch,
                        newReducer: actionMap[dialogConfig.reducerActionsCNTR[0]],
                        newValue: isOcean
                            ? {
                                  hblId: tabId,
                                  cntr: isOex
                                      ? { id: '', cntr_number: '', seal_number: '', volume: '' }
                                      : { id: '', cntr_number: '', volume: '' },
                              }
                            : isTrucking
                            ? { id: '', alias: '', quantity: 0, il_opus_volume_unit_id: '', weight: 0, reference: '' }
                            : {
                                  hawbId: tabId,
                                  alias: {
                                      id: '',
                                      alias: '',
                                      il_opus_vessel_id: '',
                                      quantity: 0,
                                      il_opus_volume_unit_id: '',
                                      weight: 0,
                                  },
                              },
                        fieldToFocusValue: isOcean ? 'cntr_number' : 'alias',
                        title: isOcean ? 'Add container' : 'Add Alias',
                    },
                }}
                disableColumnSelector={false}
                disableSelectionOnClick
                cellSelection
                rowSelection={false}
                sx={DATAGRID_CONFIG}
                {...(gridApiRef.current !== null ? { apiRef: gridApiRef } : {})}
                onCellKeyDown={handleTabKeyDown(gridApiRef, 'row')}
                onRowEditStart={handleRowEditStart}
            />
        </>
    );
}
